import React from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { Typography, Box, Breadcrumbs, Link } from '@mui/material';
import { IconChevronRight } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { SuperAdminAuthenticatedPath } from '@shared/constants/routes.constants';
import { useAgentProfileListControllerAgentProfileListForAdminsQuery } from '@shared/services/apiService/apiService';
import { AccessControlTabs } from '@features/Admin/AccessControl';
import { AgentProfilesTabContent } from './tabs/AgentProfilesTabContent';

export const EditCompanyPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get('id') ?? '';
  const companyName = searchParams.get('name') ?? '';

  const { t } = useTranslation();
  // Same query as the tab itself, so RTK Query shares one request.
  const { data: agentProfiles } = useAgentProfileListControllerAgentProfileListForAdminsQuery(
    { id: companyId },
    { skip: !companyId },
  );

  return (
    <Box sx={(theme) => ({ paddingY: theme.spacing(3) })}>
      <Breadcrumbs
        separator={<IconChevronRight size={14} />}
        sx={{
          mb: 1,
          // A router link and plain text get different line boxes; centre both on the separator.
          '& .MuiBreadcrumbs-li, & .MuiBreadcrumbs-separator': {
            display: 'flex',
            alignItems: 'center',
            lineHeight: 1.5,
          },
        }}>
        <Link
          component={RouterLink}
          to={SuperAdminAuthenticatedPath.COMPANIES}
          underline="hover"
          color="text.secondary"
          variant="body2">
          {t('superAdminMainPage.menuItems.companies')}
        </Link>
        <Typography variant="body2" color="text.primary">
          {companyName}
        </Typography>
      </Breadcrumbs>
      <Typography variant="h4" fontWeight={600} sx={{ mb: 2 }}>
        {companyName}
      </Typography>
      {companyId && (
        <AccessControlTabs
          managedCompanyId={companyId}
          extraTabs={[
            {
              key: 'agentProfiles',
              label: t('superAdminMainPage.companies.tabs.agentProfiles'),
              count: agentProfiles?.length,
              content: <AgentProfilesTabContent companyId={companyId} />,
            },
          ]}
        />
      )}
    </Box>
  );
};
